import type { AysCodeResponse} from '../code';
import { AysCode, normalizeAvailableYears } from '../code';
import { AysCodeDrgInekTable } from '../inek/inek-table';

interface AysDrgSpecificCodeResponse extends AysCodeResponse {
  mainDepartment?: AysDrgSpecificCodeMainDepartment;
  belegabteilung?: AysDrgSpecificCodeBelegabteilung | null;
  mainDiagnoses?: { code: AysCodeResponse; percentage: number }[];
  procedures?: { code: AysCodeResponse; percentage: number }[];
  costTable?: { [department: string]: { [section: string]: number } };
}

export class AysDrgSpecificCode extends AysCode {
  public static override fromObject(
    obj: AysCodeResponse,
  ): AysDrgSpecificCode {
    const drgObj = obj as AysDrgSpecificCodeResponse;
    let diagnoses: AysDrgCodeReference[] = [];
    let procedures: AysDrgCodeReference[] = [];
    let costTable: AysCodeDrgInekTable = null;

    if (drgObj.mainDiagnoses) {
      diagnoses = drgObj.mainDiagnoses.map((mainDiagnosis) => {
        return {
          code: AysCode.fromObject(mainDiagnosis.code),
          percentage: mainDiagnosis.percentage,
        };
      });
    }

    if (drgObj.procedures) {
      procedures = drgObj.procedures.map((procedure) => {
        return {
          code: AysCode.fromObject(procedure.code),
          percentage: procedure.percentage,
        };
      });
    }

    if (drgObj.costTable) {
      costTable = new AysCodeDrgInekTable(drgObj.costTable);
    }

    return new AysDrgSpecificCode(
      drgObj.name,
      drgObj.description,
      drgObj.year,
      normalizeAvailableYears(drgObj.availableYears),
      drgObj.mainDepartment,
      drgObj.belegabteilung ?? undefined,
      diagnoses,
      procedures,
      costTable,
    );
  }

  constructor(
    name: string,
    description: string,
    year: number,
    availableYears?: number[],
    public mainDepartment?: AysDrgSpecificCodeMainDepartment,
    public belegabteilung?: AysDrgSpecificCodeBelegabteilung,
    public mainDiagnoses?: AysDrgCodeReference[],
    public procedures?: AysDrgCodeReference[],
    public costTable?: AysCodeDrgInekTable,
  ) {
    super(name, description, 'G-DRG', 'specific', year, availableYears);
  }

  public override clone(): AysDrgSpecificCode {
    return new AysDrgSpecificCode(
      this.name,
      this.description,
      this.year,
      this.availableYears ? [...this.availableYears] : undefined,
      this.mainDepartment
        ? JSON.parse(JSON.stringify(this.mainDepartment))
        : undefined,
      this.belegabteilung
        ? JSON.parse(JSON.stringify(this.belegabteilung))
        : undefined,
      this.mainDiagnoses
        ? [
            ...this.mainDiagnoses.map((it) => ({
              code: it.code.clone(),
              percentage: it.percentage,
            })),
          ]
        : undefined,
      this.procedures
        ? [
            ...this.procedures.map((it) => ({
              code: it.code.clone(),
              percentage: it.percentage,
            })),
          ]
        : undefined,
      this.costTable ? this.costTable.clone() : undefined,
    );
  }
}

export interface AysDrgCodeReference {
  code: AysCode;
  percentage: number;
}

export interface AysDrgSpecificCodeMainDepartment {
  mdExceptionReuptake?: boolean;
  mdLowerStayDay?: number;
  mdLowerStayRatio?: number;
  mdShiftFixedRate?: boolean;
  mdShiftRatio?: number;
  mdUpperStayDay?: number;
  mdUpperStayRatio?: number;
  mittlereVerweildauer?: number;
  rdrgRelativgewicht?: number;
  relativgewicht?: number;
  vorhalteRelativgewicht?: number;
}

/**
 * The four Belegabteilung Bewertungsrelationen, one per Abteilungsart
 * (Fabtyp). A value is `null` when the catalog prints `-` for that
 * Abteilungsart — a `null` is never a Hauptabteilung fall-back.
 */
export interface AysDrgSpecificCodeBelegabteilungRelativgewichte {
  belegoperateur?: number | null;
  belegoperateurAnaesthesist?: number | null;
  belegoperateurHebamme?: number | null;
  belegoperateurAnaesthesistHebamme?: number | null;
}

/**
 * The Belegabteilung (BA) catalog row — a sibling of `mainDepartment`,
 * not a variant of it. Mirror fields use the `bd*` prefix, parallel to
 * the Hauptabteilung's `md*`. No vorhalte-/rdrgRelativgewicht for BA.
 */
export interface AysDrgSpecificCodeBelegabteilung {
  mittlereVerweildauer?: number;
  bdExceptionReuptake?: boolean;
  bdLowerStayDay?: number;
  bdLowerStayRatio?: number;
  bdUpperStayDay?: number;
  bdUpperStayRatio?: number;
  bdShiftFixedRate?: boolean;
  bdShiftRatio?: number;
  bdNursingRatioPerDay?: number;
  nursingProceedPerDay?: number;
  relativgewichte?: AysDrgSpecificCodeBelegabteilungRelativgewichte;
}

/**
 * One year's entry in the main-department timeseries. Since the HA/BA epic
 * each year wraps the Hauptabteilung row plus an optional Belegabteilung
 * sibling (`belegabteilung` is `null` for years before 2024 and for
 * HA-only DRGs).
 */
export interface AysDrgCodeMainDepartmentTimeSeriesEntry {
  mainDepartment?: AysDrgSpecificCodeMainDepartment;
  belegabteilung?: AysDrgSpecificCodeBelegabteilung | null;
}

export class AysDrgCodeMainDepartmentTimeSeries {
  constructor(
    public timeSeries: {
      [year: string]: AysDrgCodeMainDepartmentTimeSeriesEntry;
    },
  ) {}
}
